import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Table,
  Form,
  InputNumber,
  Select,
  message,
  Row,
  Col,
  Typography,
  Divider,
  Tag,
  Space,
  Breadcrumb,
  Statistic,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  SaveOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api/axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
const { Title, Text } = Typography;
const { Option } = Select;

const ViewPurchaseOrder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [po, setPO] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [editingItems, setEditingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const mockSuppliers = [
      { id: 1, name: 'Global Auto Parts Ltd.', contact: 'Mr. Lee', country: 'China' },
      { id: 2, name: 'Euro Spares Inc.', contact: 'Ms. Schmidt', country: 'Germany' },
      { id: 3, name: 'American Machinery Co.', contact: 'Mr. Smith', country: 'USA' },
    ];
    setSuppliers(mockSuppliers);
    fetchPO();
  }, [id]);

  const fetchPO = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/purchase-orders/${id}`);
      const data = res.data;
      setPO(data);
      setEditingItems(
        (data.items || []).map((i) => ({
          ...i,
          receiveNow: 0,
        }))
      );
    } catch {
      messageApi.error('Failed to fetch purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItems = async () => {
    if (!po) return;
    try {
      const payload = {
        items: editingItems.map((i) => ({
          id: i.id,
          quantity: i.quantity,
          unitCost: i.unitCost,
        })),
      };
      await api.put(`/api/purchase-orders/${po.id}/items`, payload);
      messageApi.success('Quantities updated');
      setIsEditing(false);
      fetchPO();
    } catch {
      messageApi.error('Failed to update quantities');
    }
  };

  const exportToPDF = () => {
    if (!po) return;
    const doc = new jsPDF();
    const supplier = suppliers.find((s) => s.id === po.supplierId) || {};
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('JS CONSTRUCTION MACHINERY', 105, 18, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('123 Industrial Way, Your City, 12345', 105, 25, { align: 'center' });
    doc.text('Tel: +1-234-567-8900  |  Email: purchase@jsconstruction.com', 105, 30, { align: 'center' });
    doc.setLineWidth(0.5);
    doc.line(14, 34, 196, 34);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PURCHASE ORDER', 105, 44, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`PO Number: ${po.purchaseOrderId || po.id}`, 14, 55);
    doc.text(`Date: ${new Date(po.orderDate || po.createdAt).toLocaleDateString()}`, 14, 62);
    doc.text(`Status: ${po.status || 'Pending'}`, 14, 69);
    autoTable(doc, {
      startY: 75,
      head: [['Supplier Information']],
      body: [[`${supplier.name || 'N/A'}\nContact: ${supplier.contact || 'N/A'}\nCountry: ${supplier.country || 'N/A'}`]],
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
    });
    const tableData = (po.items || []).map((item) => [
      item.sparepart?.importerId || item.importerId || '—',
      item.sparepart?.partName || item.partName || '—',
      item.sparepart?.partNumber || item.partNumber || '—',
      item.quantity,
      item.sparepart?.measureUnit || item.measureUnit || 'pcs',
      `$${(item.unitCost || 0).toFixed(2)}`,
      `$${((item.quantity || 0) * (item.unitCost || 0)).toFixed(2)}`,
    ]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [['Importer ID', 'Part Name', 'Part Number', 'Qty', 'UOM', 'Unit Cost', 'Line Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [22, 160, 133], fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 45 }, 2: { cellWidth: 30 }, 3: { cellWidth: 15 }, 4: { cellWidth: 15 }, 5: { cellWidth: 22 }, 6: { cellWidth: 25 } },
    });
    const finalY = doc.lastAutoTable.finalY + 10;
    const shipping = po.shippingCost || 0;
    const grand = (po.totalCost || 0) + shipping;
    doc.setFontSize(11);
    doc.text(`Subtotal: $${(po.totalCost || 0).toFixed(2)}`, 140, finalY);
    doc.text(`Shipping: $${shipping.toFixed(2)}`, 140, finalY + 7);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`GRAND TOTAL: $${grand.toFixed(2)}`, 140, finalY + 16);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Shipping Terms: ${po.shippingTerms || 'N/A'}`, 14, finalY + 10);
    doc.text(`Notes: ${po.description || 'N/A'}`, 14, finalY + 17);
    const pageHeight = doc.internal.pageSize.height;
    doc.setLineWidth(0.3);
    doc.line(14, pageHeight - 25, 196, pageHeight - 25);
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text('JS Construction Machinery – Authorized Purchase Order', 105, pageHeight - 18, { align: 'center' });
    doc.text('This document is computer generated and does not require a signature.', 105, pageHeight - 13, { align: 'center' });
    doc.save(`${po.purchaseOrderId || 'PO'}.pdf`);
  };

  const exportPOToExcel = () => {
    if (!po) return;
    const supplier = suppliers.find((s) => s.id === po.supplierId) || {};
    const headerData = [
      ['PURCHASE ORDER'],
      [],
      ['PO Number', po.purchaseOrderId || po.id],
      ['Date', po.orderDate ? new Date(po.orderDate).toLocaleDateString() : ''],
      ['Status', po.status || 'Pending'],
      ['Supplier', supplier.name || 'N/A'],
      ['Currency', po.currency || 'USD'],
      ['Shipping Cost', po.shippingCost || 0],
      ['Shipping Terms', po.shippingTerms || ''],
      ['Notes', po.description || ''],
      [],
      ['Importer ID', 'Part Name', 'Part Number', 'Ordered Qty', 'Received Qty', 'UOM', 'Unit Cost', 'Line Total'],
    ];
    const itemsData = (po.items || []).map((item) => [
      item.sparepart?.importerId || '',
      item.sparepart?.partName || '',
      item.sparepart?.partNumber || '',
      item.quantity || 0,
      item.receivedQty || 0,
      item.sparepart?.measureUnit || 'pcs',
      item.unitCost || 0,
      ((item.quantity || 0) * (item.unitCost || 0)).toFixed(2),
    ]);
    const totalRow = [
      ['', '', '', '', '', '', 'Subtotal', (po.totalCost || 0).toFixed(2)],
      ['', '', '', '', '', '', 'Shipping', (po.shippingCost || 0).toFixed(2)],
      ['', '', '', '', '', '', 'Grand Total', ((po.totalCost || 0) + (po.shippingCost || 0)).toFixed(2)],
    ];
    const finalData = [...headerData, ...itemsData, ...totalRow];
    const worksheet = XLSX.utils.aoa_to_sheet(finalData);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 30 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 12 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Order');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${po.purchaseOrderId || 'PO'}.xlsx`);
  };

  const itemColumns = [
    {
      title: 'Importer ID',
      dataIndex: ['sparepart', 'importerId'],
      render: (val) => <Text strong>{val || '—'}</Text>,
    },
    {
      title: 'Part Name',
      dataIndex: ['sparepart', 'partName'],
      render: (val) => <Text>{val || '—'}</Text>,
    },
    {
      title: 'Part Number',
      dataIndex: ['sparepart', 'partNumber'],
    },
    {
      title: 'Ordered Qty',
      dataIndex: 'quantity',
      width: 120,
      render: (val, record) => (
        <InputNumber
          min={1}
          value={val}
          disabled={!isEditing}
          onChange={(v) => {
            setEditingItems((prev) =>
              prev.map((i) => (i.id === record.id ? { ...i, quantity: v } : i))
            );
          }}
        />
      ),
    },
    {
      title: 'Received Qty',
      dataIndex: 'receivedQty',
      width: 120,
      render: (v) => <Tag color="green">{v || 0}</Tag>,
    },
    {
      title: 'UOM',
      dataIndex: ['sparepart', 'measureUnit'],
      render: (u) => u || 'pcs',
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unitCost',
      width: 120,
      render: (val, record) => (
        <InputNumber
          min={0}
          value={val}
          disabled={!isEditing}
          formatter={(v) => `$ ${v}`}
          parser={(v) => v.replace(/\$\s?|(,*)/g, '')}
          onChange={(v) => {
            setEditingItems((prev) =>
              prev.map((i) => (i.id === record.id ? { ...i, unitCost: v } : i))
            );
          }}
        />
      ),
    },
    {
      title: 'Line Total',
      key: 'lineTotal',
      width: 120,
      render: (_, record) => {
        const total = (record.quantity || 0) * (record.unitCost || 0);
        return <Text strong style={{ color: '#52c41a' }}>${total.toFixed(2)}</Text>;
      },
    },
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100 }}>Loading...</div>;
  }

  if (!po) {
    return <div style={{ textAlign: 'center', padding: 100 }}>Purchase Order not found</div>;
  }

  const supplier = suppliers.find((s) => s.id === po.supplierId);
  const totalReceived = editingItems.reduce((sum, i) => sum + (i.receivedQty || 0), 0);
  const totalOrdered = editingItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
  const receiveProgress = totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#f0f2f5' }}>
      {contextHolder}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/purchase-order')}>
            Purchase Orders
          </Button>
        </Breadcrumb.Item>
        <Breadcrumb.Item>View PO</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Card */}
      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          marginBottom: 24,
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {po.purchaseOrderId || po.id}
                </Title>
                <Text type="secondary">Purchase Order Details</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/purchase-order')}>
                Back
              </Button>
              <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
                PDF
              </Button>
              <Button icon={<FileExcelOutlined />} onClick={exportPOToExcel}>
                Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Stats Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Status" value={po.status || 'Pending'} />
            <Tag color={po.status === 'Completed' ? 'green' : 'orange'} style={{ marginTop: 8 }}>
              {po.status || 'Pending'}
            </Tag>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Total Items" value={po.items?.length || 0} suffix="items" />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Total Cost" prefix="$" value={(po.totalCost || 0).toFixed(2)} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Shipping" prefix="$" value={(po.shippingCost || 0).toFixed(2)} />
          </Card>
        </Col>
      </Row>

      {/* Progress Card */}
      <Card title="Receiving Progress" style={{ marginBottom: 24 }}>
        <Row align="middle">
          <Col flex="auto">
            <Progress percent={receiveProgress} status={receiveProgress === 100 ? 'success' : 'active'} />
          </Col>
          <Col>
            <Text>
              {totalReceived} / {totalOrdered} received
            </Text>
          </Col>
        </Row>
      </Card>

      {/* PO Details */}
      <Card
        title="Order Information"
        style={{ marginBottom: 24 }}
        extra={
          isEditing ? (
            <Space>
              <Button onClick={() => { setIsEditing(false); fetchPO(); }}>Cancel</Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleUpdateItems}>
                Save Changes
              </Button>
            </Space>
          ) : (
            <Button type="primary" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
              Edit Items
            </Button>
          )
        }
      >
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Text strong>Supplier:</Text>
            <br />
            {supplier ? (
              <Space direction="vertical" size={0}>
                <Text>{supplier.name}</Text>
                <Text type="secondary">{supplier.contact} | {supplier.country}</Text>
              </Space>
            ) : (
              'N/A'
            )}
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Order Date:</Text>
            <br />
            <Text>{po.orderDate ? new Date(po.orderDate).toLocaleDateString() : '—'}</Text>
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Currency:</Text>
            <br />
            <Tag color="blue">{po.currency || 'USD'}</Tag>
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Shipping Terms:</Text>
            <br />
            <Text>{po.shippingTerms || 'N/A'}</Text>
          </Col>
          <Col xs={24} md={8}>
            <Text strong>Notes:</Text>
            <br />
            <Text>{po.description || 'N/A'}</Text>
          </Col>
        </Row>
      </Card>

      {/* Items Table */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            <Text strong>Order Items ({editingItems.length})</Text>
          </Space>
        }
      >
        <Table
          columns={itemColumns}
          dataSource={editingItems}
          rowKey="id"
          pagination={false}
          size="middle"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <Text strong>{totalOrdered}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Text strong>{totalReceived}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={2}></Table.Summary.Cell>
                <Table.Summary.Cell index={7}>
                  <Text strong style={{ color: '#52c41a' }}>
                    ${po.totalCost?.toFixed(2) || '0.00'}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </div>
  );
};

export default ViewPurchaseOrder;
